import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Stack,
  IconButton,
  Typography,
  CssBaseline,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import GameLogModal from '../components/GameLogModal';

// Custom dark theme matching Pickem
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const Projections = ({ projections, nbaProjections, userName, isAuthenticated }) => {
  // Load league selection from localStorage, default to NBA (3)
  const [selectedLeague, setSelectedLeague] = useState(() => {
    const saved = localStorage.getItem('projections_selectedLeague');
    return saved ? parseInt(saved, 10) : 3; // Default to NBA
  });
  
  const [selectedPosition, setSelectedPosition] = useState('All');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'Points', sort: 'desc' }]); // Default to Points for NBA
  const [searchQuery, setSearchQuery] = useState('');

  // Game Log Modal State
  const [gameLogModalOpen, setGameLogModalOpen] = useState(false);
  const [selectedPlayerData, setSelectedPlayerData] = useState(null);
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);

  // Save league selection to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('projections_selectedLeague', selectedLeague.toString());
  }, [selectedLeague]);

  // Position tabs - conditional based on league
  const positionTabs = useMemo(() => {
    if (selectedLeague === 3) { // NBA
      return ['All', 'G', 'F', 'C'];
    }
    // NFL/CFB
    return ['All', 'QB', 'RB', 'WR', 'TE'];
  }, [selectedLeague]);

  // Reset position when league changes
  useEffect(() => {
    setSelectedPosition('All');
    setCurrentPage(0);
  }, [selectedLeague]);

  // Select active data source based on league
  const activeProjections = useMemo(() => {
    if (selectedLeague === 3) {
      return nbaProjections || [];
    }
    return projections || [];
  }, [selectedLeague, projections, nbaProjections]);

  // Handle player name click to fetch game log
  const handlePlayerClick = useCallback(async (playerRow) => {
    // Only fetch game logs for NFL (1) and NBA (3) leagues
    if (selectedLeague !== 1 && selectedLeague !== 3) return;
    
    const personId = playerRow.PersonId;
    
    // Don't proceed if no PersonId
    if (!personId) {
      console.log('No PersonId for this player');
      return;
    }

    setGameLogLoading(true);
    
    try {
      // Determine the correct API endpoint based on league
      const league = selectedLeague === 3 ? 'nba' : 'nfl';
      const response = await fetch(`https://ws.betpros.ai:8080/api/player-game-logs/${league}/${personId}`);
      
      if (!response.ok) {
        console.error('Failed to fetch game log:', response.status);
        setGameLogLoading(false);
        return;
      }
      
      const data = await response.json();
      
      // Only open modal if we have game log data
      if (data && data.gameLogs && data.gameLogs.length > 0) {
        setSelectedPlayerData(playerRow);
        setGameLogData(data.gameLogs);
        setGameLogModalOpen(true);
      } else {
        console.log('No game log data available for this player');
      }
    } catch (error) {
      console.error('Error fetching game log:', error);
    } finally {
      setGameLogLoading(false);
    }
  }, [selectedLeague]);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setGameLogModalOpen(false);
    setSelectedPlayerData(null);
    setGameLogData(null);
  }, []);

  // Shared columns (Position, Player, Event)
  const sharedColumns = useMemo(() => [
    {
      field: 'Position',
      headerName: 'Pos',
      flex: 0.5,
      minWidth: 60,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pos
        </Typography>
      ),
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'PlayerName',
      headerName: 'Player',
      flex: 2,
      minWidth: 180,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Player
        </Typography>
      ),
      renderCell: (params) => {
        if (!params?.row) return '-';
        const isNFL = selectedLeague === 1;
        const isNBA = selectedLeague === 3;
        const hasPersonId = params.row.PersonId;
        const isClickable = (isNFL || isNBA) && hasPersonId;
        
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            height: '100%',
            width: '100%'
          }}>
            {params.row.teamLogo && (
              <img 
                src={params.row.teamLogo} 
                alt="" 
                style={{ width: 20, height: 20, objectFit: 'contain' }}
              />
            )}
            <Typography 
              onClick={isClickable ? (e) => {
                e.stopPropagation();
                handlePlayerClick(params.row);
              } : undefined}
              sx={{ 
                fontSize: '0.875rem',
                cursor: isClickable ? 'pointer' : 'default',
                '&:hover': isClickable ? {
                  textDecoration: 'underline'
                } : {}
              }}
            >
              {params.value || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'Event',
      headerName: 'Event',
      flex: 1.5,
      minWidth: 180,
      sortComparator: (v1, v2, param1, param2) => {
        const date1 = param1.api.getCellValue(param1.id, 'EventStart');
        const date2 = param2.api.getCellValue(param2.id, 'EventStart');
        
        if (!date1 && !date2) return 0;
        if (!date1) return 1;
        if (!date2) return -1;
        
        return new Date(date1).getTime() - new Date(date2).getTime();
      },
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        if (!params?.row) return '-';
        
        const away = params.row.AwayShort || '';
        const home = params.row.HomeShort || '';
        const eventStart = params.row.EventStart
          ? new Date(params.row.EventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        
        return (
          <Box sx={{ 
            whiteSpace: 'pre-line', 
            textAlign: 'left', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            height: '100%' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
              {params.row.AwayLogo && (
                <img
                  src={params.row.AwayLogo}
                  alt={`${away} Logo`}
                  style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{away}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px' }}>@</Typography>
              {params.row.HomeLogo && (
                <img
                  src={params.row.HomeLogo}
                  alt={`${home} Logo`}
                  style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{home}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#aaaaaa', paddingLeft: '8px' }}>
              {eventStart}
            </Typography>
          </Box>
        );
      },
    },
  ], [selectedLeague, handlePlayerClick]);

  // NBA-specific stat columns
  const nbaStatColumns = [
    {
      field: 'Points',
      headerName: 'Points',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Points
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Rebounds',
      headerName: 'Rebounds',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Rebounds
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Assists',
      headerName: 'Assists',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Assists
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'ThreePointers',
      headerName: '3PT',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          3PT
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Turnovers',
      headerName: 'Turnovers',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Turnovers
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Blocks',
      headerName: 'Blocks',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Blocks
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
    {
      field: 'Steals',
      headerName: 'Steals',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Steals
        </Typography>
      ),
      valueFormatter: (value) => value != null ? value.toFixed(2) : '-',
    },
  ];

  // NFL-specific stat columns  
  const nflStatColumns = [
    {
      field: 'PassingYards',
      headerName: 'Pass Yds',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass Yds</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'PassAttempts',
      headerName: 'Pass Att',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass Att</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'Completions',
      headerName: 'Pass Comp',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass Comp</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'PassingTD',
      headerName: 'Pass TD',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Pass TD</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'Interceptions',
      headerName: 'INT',
      type: 'number',
      flex: 0.8,
      minWidth: 70,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>INT</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'RushAttempts',
      headerName: 'Rush Att',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rush Att</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'RushingYards',
      headerName: 'Rush Yds',
      type: 'number',
      flex: 1,
      minWidth: 100,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rush Yds</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'Receptions',
      headerName: 'Rec',
      type: 'number',
      flex: 0.8,
      minWidth: 70,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rec</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'ReceivingYards',
      headerName: 'Rec Yds',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Rec Yds</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(1) : '-'),
    },
    {
      field: 'Touchdowns',
      headerName: 'Total TD',
      type: 'number',
      flex: 0.8,
      minWidth: 80,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Total TD</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'halfPPR',
      headerName: '0.5 PPR',
      type: 'number',
      flex: 1,
      minWidth: 90,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>0.5 PPR</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
    {
      field: 'fullPPR',
      headerName: 'PPR',
      type: 'number',
      flex: 0.8,
      minWidth: 80,
      renderHeader: () => <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>PPR</Typography>,
      valueFormatter: (value) => (value != null ? value.toFixed(2) : '-'),
    },
  ];


  // Choose columns based on selected league
  const columns = useMemo(() => {
    if (selectedLeague === 3) {
      return [...sharedColumns, ...nbaStatColumns];
    }
    return [...sharedColumns, ...nflStatColumns];
  }, [selectedLeague]);

  // Process projections with filters
  const processedProjections = useMemo(() => {
    if (!Array.isArray(activeProjections)) return [];
    
    // Filter by league first
    let filtered = activeProjections.filter(proj => {
      const projLeagueId = Number(proj?.LeagueId || proj?.leagueId);
      return projLeagueId === Number(selectedLeague);
    });
    
    // Filter by position - intelligent filtering for NBA
    if (selectedPosition !== 'All') {
      if (selectedLeague === 3) { // NBA - intelligent position filtering
        filtered = filtered.filter(proj => {
          const position = proj?.Position || '';
          // Check if the position string contains the selected position letter
          // e.g., "G-F" contains both "G" and "F", so it will show up in both tabs
          return position.includes(selectedPosition);
        });
      } else { // NFL/CFB - exact match
        filtered = filtered.filter(proj => 
          proj?.Position === selectedPosition
        );
      }
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term);
      filtered = filtered.filter(proj => {
        const playerName = (proj.PlayerName || '').toLowerCase();
        const awayTeam = (proj.AwayShort || '').toLowerCase();
        const homeTeam = (proj.HomeShort || '').toLowerCase();
        const searchableText = `${playerName} ${awayTeam} ${homeTeam}`;
        
        return searchTerms.every(term => searchableText.includes(term));
      });
    }
    
    // Add unique IDs and calculate fantasy values for NFL
    return filtered.map((proj, idx) => {
      const processedProj = {
        ...proj,
        id: `proj-${idx}-${proj?.PersonId || 'na'}`,
      };

      // Only calculate fantasy points for NFL
      if (selectedLeague !== 3) {
        // Calculate halfPPR value if not already present
        if (processedProj['1/2PPR'] == null) {
          if (processedProj.Position === 'QB') {
            const passYds = processedProj.PassingYards;
            const passTd = processedProj.PassingTD;
            const int = processedProj.Interceptions;
            const rushYds = processedProj.RushingYards;
            const totalTd = processedProj.Touchdowns;
            
            if (passYds != null && passTd != null && int != null && rushYds != null && totalTd != null) {
              processedProj.halfPPR = (passYds / 25) + (4 * passTd) - int + (0.1 * rushYds) + (6 * totalTd);
            } else {
              processedProj.halfPPR = null;
            }
          } else if (processedProj.Position === 'RB') {
            const rushYds = processedProj.RushingYards;
            const recYds = processedProj.ReceivingYards;
            const rec = processedProj.Receptions;
            const totalTd = processedProj.Touchdowns;
            
            if (rushYds != null && recYds != null && rec != null && totalTd != null) {
              processedProj.halfPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + (0.5 * rec);
            } else {
              processedProj.halfPPR = null;
            }
          } else if (processedProj.Position === 'WR' || processedProj.Position === 'TE') {
            const rushYds = processedProj.RushingYards || 0;
            const recYds = processedProj.ReceivingYards;
            const rec = processedProj.Receptions;
            const totalTd = processedProj.Touchdowns;
            
            if (recYds != null && rec != null && totalTd != null) {
              processedProj.halfPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + (0.5 * rec);
            } else {
              processedProj.halfPPR = null;
            }
          } else {
            processedProj.halfPPR = null;
          }
        } else {
          processedProj.halfPPR = processedProj['1/2PPR'];
        }

        // Calculate PPR value if not already present
        if (processedProj.PPR == null) {
          if (processedProj.Position === 'QB') {
            const passYds = processedProj.PassingYards;
            const passTd = processedProj.PassingTD;
            const int = processedProj.Interceptions;
            const rushYds = processedProj.RushingYards;
            const totalTd = processedProj.Touchdowns;
            
            if (passYds != null && passTd != null && int != null && rushYds != null && totalTd != null) {
              processedProj.fullPPR = (passYds / 25) + (4 * passTd) - int + (0.1 * rushYds) + (6 * totalTd);
            } else {
              processedProj.fullPPR = null;
            }
          } else if (processedProj.Position === 'RB') {
            const rushYds = processedProj.RushingYards;
            const recYds = processedProj.ReceivingYards;
            const rec = processedProj.Receptions;
            const totalTd = processedProj.Touchdowns;
            
            if (rushYds != null && recYds != null && rec != null && totalTd != null) {
              processedProj.fullPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + rec;
            } else {
              processedProj.fullPPR = null;
            }
          } else if (processedProj.Position === 'WR' || processedProj.Position === 'TE') {
            const rushYds = processedProj.RushingYards || 0;
            const recYds = processedProj.ReceivingYards;
            const rec = processedProj.Receptions;
            const totalTd = processedProj.Touchdowns;
            
            if (recYds != null && rec != null && totalTd != null) {
              processedProj.fullPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + rec;
            } else {
              processedProj.fullPPR = null;
            }
          } else {
            processedProj.fullPPR = null;
          }
        } else {
          processedProj.fullPPR = processedProj.PPR;
        }
      }

      return processedProj;
    });
  }, [activeProjections, selectedPosition, searchQuery, selectedLeague]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    const newPosition = positionTabs[newValue];
    setSelectedPosition(newPosition);
    setCurrentPage(0);
    
    // Update sort based on position and league
    let newSortField = 'PassingYards'; // Default for NFL
    
    if (selectedLeague === 3) { // NBA
      newSortField = 'Points'; // Default for all NBA positions
    } else { // NFL/CFB
      if (newPosition === 'RB') {
        newSortField = 'RushingYards';
      } else if (newPosition === 'WR' || newPosition === 'TE') {
        newSortField = 'ReceivingYards';
      }
    }
    
    setSortModel([{ field: newSortField, sort: 'desc' }]);
  };

  // Update sort model when league changes
  useEffect(() => {
    if (selectedLeague === 3) { // NBA
      setSortModel([{ field: 'Points', sort: 'desc' }]);
    } else { // NFL/CFB
      setSortModel([{ field: 'PassingYards', sort: 'desc' }]);
    }
  }, [selectedLeague]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    if (!isAuthenticated) {
      alert('Please log in to export data.');
      return;
    }

    if (processedProjections.length === 0) {
      alert('No data to export');
      return;
    }

    const leagueName = selectedLeague === 3 ? 'NBA' : selectedLeague === 1 ? 'NFL' : 'CFB';
    
    const headers = columns
      .filter(col => col.field !== 'id')
      .map(col => col.headerName || col.field);
    
    const rows = processedProjections.map(proj => 
      columns
        .filter(col => col.field !== 'id')
        .map(col => {
          const value = proj[col.field];
          if (value == null) return '';
          if (typeof value === 'number') return value;
          return `"${String(value).replace(/"/g, '""')}"`;
        })
    );

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${leagueName}_projections_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [processedProjections, columns, isAuthenticated, selectedLeague]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ 
          width: '100%', 
          height: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Filter Section */}
          <Box className="filter-section" sx={{ 
            padding: '16px',
            backgroundColor: '#121212',
            borderBottom: '1px solid #333'
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                {/* League Dropdown */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>
                    League
                  </InputLabel>
                  <Select
                    labelId="league-filter-label"
                    label="League"
                    notched
                    value={selectedLeague}
                    onChange={(e) => {
                      setSelectedLeague(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    <MenuItem value={1}>NFL</MenuItem>
                    <MenuItem value={2}>CFB</MenuItem>
                    <MenuItem value={3}>NBA</MenuItem>
                  </Select>
                </FormControl>
                
                {/* Position Tabs */}
                <Tabs 
                  value={positionTabs.indexOf(selectedPosition)}
                  onChange={handleTabChange}
                  sx={{
                    minHeight: 38,
                    '& .MuiTab-root': { 
                      color: '#ffffff',
                      minWidth: 60,
                      minHeight: 32,
                      padding: '4px 12px',
                      '&.Mui-selected': { color: '#ff1744' }
                    },
                    '& .MuiTabs-indicator': { backgroundColor: '#ff1744' }
                  }}
                >
                  {positionTabs.map((position) => (
                    <Tab key={position} label={position} />
                  ))}
                </Tabs>
                
                {/* Search Bar */}
                <TextField
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search player or matchup"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#BBBBBB' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 225,
                    '& .MuiInputBase-input': { 
                      color: '#ffffff' 
                    },
                    '& .MuiInputBase-input::placeholder': { 
                      color: '#BBBBBB', 
                      opacity: 1 
                    },
                    '& .MuiInputLabel-root': { 
                      fontSize: '0.875rem' 
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ffffff',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ff1744',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff1744',
                      },
                    },
                  }}
                />
              </Stack>
              
              <Tooltip 
                title={
                  !isAuthenticated 
                    ? "Please log in to enable exports" 
                    : processedProjections.length === 0 
                    ? "No data to export" 
                    : ""
                }
                arrow
              >
                <span>
                  <IconButton
                    onClick={exportToCSV}
                    disabled={processedProjections.length === 0 || !isAuthenticated}
                    sx={{
                      color: '#bbbbbb',
                      padding: '4px',
                      '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                      '&.Mui-disabled': { color: '#666666' },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {/* Data Grid */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Box className="table-section" sx={{ height: '100%' }}>
              <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                <DataGridPro
                  rows={processedProjections}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  rowsPerPageOptions={[50, 100, 250]}
                  pagination
                  paginationMode="client"
                  page={currentPage}
                  onPageChange={setCurrentPage}
                  sortingMode="client"
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnMenu
                  loading={!activeProjections || activeProjections.length === 0}
                  getRowId={(row) => row.id}
                  hideFooterSelectedRowCount
                  sx={{
                    '& .MuiDataGrid-columnHeaders': { 
                      backgroundColor: '#1e1e1e', 
                      borderBottom: '2px solid #444',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                    },
                    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: '#ffffff !important',
                      userSelect: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                      userSelect: 'none',
                      borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-cell:last-child': {
                      borderRight: 'none',
                    },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                    '& .MuiDataGrid-row.Mui-selected': {
                      backgroundColor: '#1a1a1a',
                      '&:hover': { backgroundColor: '#1a1a1a' },
                    },
                    '& .MuiDataGrid-columnSeparator': { 
                      visibility: 'visible',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: '#ffffff !important',
                    },
                    '& .MuiSelect-icon': { color: '#ffffff !important' },
                    '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                    '& .MuiDataGrid-root': {
                      overflowY: 'auto !important',
                      margin: 0,
                      boxSizing: 'border-box',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      overflowY: 'auto !important',
                    },
                    '& .MuiDataGrid-main': {
                      padding: 0,
                      boxSizing: 'border-box',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: '#1e1e1e',
                      color: '#ffffff !important',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      boxSizing: 'border-box',
                    },
                  }}
                  rowBuffer={10}
                  rowThreshold={5}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Game Log Modal */}
        <GameLogModal
          open={gameLogModalOpen}
          onClose={handleCloseModal}
          playerData={selectedPlayerData}
          gameLogData={gameLogData}
          loading={gameLogLoading}
          sport={selectedLeague === 3 ? 'NBA' : 'NFL'}
        />
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Projections;